import * as React from "react"

import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react"

import { cn } from "../../cn"

const toastVariants = cva(
  [
    "group pointer-events-auto relative flex w-full gap-2.5 rounded-lg border border-coval-border bg-coval-surface-raised px-3.5 py-3 pr-10 text-sm text-coval-text shadow-lg",
    "transition-all",
    "data-[state=open]:animate-toast-in",
    "data-[state=closed]:animate-toast-out",
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform",
    "data-[swipe=end]:animate-toast-swipe-out",
  ],
  {
    variants: {
      variant: {
        info: "",
        success: "",
        warning: "",
        danger: "",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
)

const toastIconVariants = cva(
  "mt-0.5 flex size-4 shrink-0 items-center justify-center [&>svg]:size-4",
  {
    variants: {
      variant: {
        info: "text-coval-info-text",
        success: "text-coval-success-text",
        warning: "text-coval-warning-text",
        danger: "text-coval-danger-text",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
)

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>["variant"]>

const defaultToastIcons = {
  info: <Info aria-hidden="true" />,
  success: <CheckCircle aria-hidden="true" />,
  warning: <AlertTriangle aria-hidden="true" />,
  danger: <AlertCircle aria-hidden="true" />,
} satisfies Record<ToastVariant, React.ReactNode>

const toastViewportVariants = cva([
  "fixed bottom-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4",
  // Anchor the right edge at the Sofia dock width (0px when the panel is
  // closed/mobile via the :root default, making this an exact no-op) so toasts
  // clear the docked panel instead of painting behind its opaque surface, which
  // stacks above them. Same contract the right-side Drawer uses.
  "right-[var(--covi-dock-width)]",
  // Cap the width at the space left of the dock so a user-widened panel pushes
  // the toast narrower rather than off the left edge of the viewport.
  "sm:max-w-[min(420px,calc(100vw-var(--covi-dock-width)))]",
])

/* ── Viewport ─────────────────────────────────────────────── */

const ToastViewport = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(toastViewportVariants(), className)}
    {...props}
  />
))

ToastViewport.displayName = "ToastViewport"

/* ── Toast ────────────────────────────────────────────────── */

export interface ToastProps
  extends
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>,
    VariantProps<typeof toastVariants> {
  icon?: React.ReactNode
}

const Toast = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Root>,
  ToastProps
>(({ className, variant, icon, children, ...props }, ref) => {
  const resolvedVariant: ToastVariant = variant ?? "info"
  const resolvedIcon = icon ?? defaultToastIcons[resolvedVariant]

  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      {resolvedIcon && (
        <span className={toastIconVariants({ variant: resolvedVariant })}>
          {resolvedIcon}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </ToastPrimitives.Root>
  )
})

Toast.displayName = "Toast"

/* ── Title ────────────────────────────────────────────────── */

const ToastTitle = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("font-medium leading-5 tracking-tight", className)}
    {...props}
  />
))

ToastTitle.displayName = "ToastTitle"

/* ── Description ──────────────────────────────────────────── */

const ToastDescription = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("mt-0.5 leading-5 text-coval-text", className)}
    {...props}
  />
))

ToastDescription.displayName = "ToastDescription"

/* ── Action ───────────────────────────────────────────────── */

const ToastAction = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "mt-2 inline-flex shrink-0 cursor-pointer items-center rounded-sm text-sm font-medium text-coval-info-text underline-offset-4",
      "hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active focus-visible:ring-offset-1",
      "disabled:cursor-default disabled:opacity-50",
      className,
    )}
    {...props}
  />
))

ToastAction.displayName = "ToastAction"

/* ── Close ────────────────────────────────────────────────── */

const ToastClose = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    aria-label="Dismiss"
    className={cn(
      "absolute right-2 top-2 shrink-0 cursor-pointer rounded-md p-1 text-coval-text-muted opacity-70 transition-colors",
      "hover:bg-surface-1 hover:text-coval-text hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active focus-visible:ring-offset-1",
      className,
    )}
    {...props}
  >
    <X className="size-4" />
  </ToastPrimitives.Close>
))

ToastClose.displayName = "ToastClose"

/* ── Provider (re-export) ─────────────────────────────────── */

const ToastProvider = ToastPrimitives.Provider

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastClose,
  toastVariants,
  toastViewportVariants,
}
